import { inject, Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { defer, firstValueFrom, of, throwError, timer } from 'rxjs';
import { catchError, map, retryWhen, switchMap, tap } from 'rxjs/operators';

import {
  CommonApiDataType,
  CreateManualTrialInput,
  GqlService,
  TrialImplementationStatus,
  TrialWorkspaceType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { Option } from '@shared/types/components.type';

import { TrialModel, TrialsStore } from './trials.store';

@Injectable({ providedIn: 'root' })
export class TrialsService {
  private gqlService = inject(GqlService);
  private overlayService = inject(OverlayService);
  private trialsStore = inject(TrialsStore);

  async createCustomTrial({
    auxilius_start_date,
    implementation_status,
    indication,
    name,
    onboarding_complete,
    program,
    project,
    short_name,
    sponsor_organization_name,
    therapy_area,
    trial_phase,
    workspace_type,
  }: CreateManualTrialInput): Promise<string | undefined> {
    this.trialsStore.setLoading(true);

    const { data, errors } = await firstValueFrom(
      this.gqlService.createManualTrial$({
        auxilius_start_date,
        implementation_status,
        indication,
        name,
        onboarding_complete,
        program,
        project,
        short_name,
        sponsor_organization_name,
        therapy_area,
        trial_phase,
        workspace_type,
      })
    );

    this.trialsStore.setLoading(false);

    if (data) {
      this.trialsStore.add({
        ...data,
        workspace_type,
      });

      return data.id;
    }

    this.overlayService.error(errors);

    return undefined;
  }

  get() {
    this.trialsStore.setLoading(true);

    return defer(() => this.gqlService.listTrials$()).pipe(
      tap((x) => {
        if (!x.success) {
          throw new Error('Failed to get list of trials');
        }
      }),
      retryWhen((err) => {
        let numRetries = 0;

        return err.pipe(
          switchMap(() => {
            if (numRetries < 3) {
              numRetries += 1;

              return timer(10 * 1000, 1e6);
            }

            return throwError('Max retry count reached');
          })
        );
      }),
      catchError(() => of({ data: [] })),
      map((x) => {
        this.trialsStore.set(x.data || []);
        this.trialsStore.setLoading(false);

        return x;
      })
    );
  }

  async getTherapyAreaOptions(): Promise<Option[]> {
    const { data } = await firstValueFrom(
      this.gqlService.getCommonApiData$(CommonApiDataType.THERAPY_AREA)
    );

    return data
      ? JSON.parse(data.data).options.map((option: { id: string; value: string }) => ({
          label: option.value,
          value: option.id,
        }))
      : [];
  }

  remove(id: ID) {
    this.trialsStore.remove(id);
  }

  async update(id: string, trial: Partial<TrialModel>) {
    this.trialsStore.update(id, trial);
    const { errors, success } = await firstValueFrom(
      this.gqlService.updateTrial$({
        auxilius_start_date: trial.auxilius_start_date || null,
        implementation_status:
          trial.implementation_status || TrialImplementationStatus.IMPLEMENTATION_STATUS_BLANK,
        indication: trial.indication,
        program: trial.program || '',
        project: trial.project || '',
        short_name: trial.short_name,
        therapy_area: trial.therapy_area,
        trial_phase: trial.trial_phase,
        workspace_type: trial.workspace_type || TrialWorkspaceType.WORKSPACE_TYPE_CLINICAL_TRIAL,
      })
    );

    if (!success && errors) {
      this.overlayService.error(errors);

      return false;
    }

    this.overlayService.success();

    return true;
  }

  async updateOnboarding(id: string, trial: Partial<TrialModel>) {
    const { errors, success } = await firstValueFrom(
      this.gqlService.updateTrial$({
        auxilius_start_date: trial.auxilius_start_date,
        implementation_status:
          trial.implementation_status || TrialImplementationStatus.IMPLEMENTATION_STATUS_BLANK,
        onboarding_complete: true,
        workspace_type: trial.workspace_type || TrialWorkspaceType.WORKSPACE_TYPE_CLINICAL_TRIAL,
      })
    );

    if (success) {
      this.trialsStore.update(id, { ...trial, onboarding_complete: true });

      return true;
    }

    alert(errors);

    return false;
  }
}
